import { JSONObject } from '@lucidtech/las-sdk-browser';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Focusable, Key, MenuTrigger, Tab as RACTab, TabList, TabPanel, TabProps, Tabs } from 'react-aria-components';

import ConnectUrlIconLarge from '@/assets/connect-url-icon-large.svg?react';
import CSVIconLarge from '@/assets/csv-icon-large.svg?react';
import {
  Badge,
  BasicTooltip,
  Button,
  Checkbox,
  Input,
  Label,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Save,
  Separator,
  Table,
  Text,
  TextField,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import {
  AddIcon,
  CSVIcon,
  LinkIcon,
  OverflowIcon,
  RotateIcon,
  SettingsIcon,
  TableIcon,
  UnlinkIcon,
} from '@/components/icons';
import { findField, FormatterValidatorHeader } from '@/components/ui';
import { useClient, useFormatterMutation, useMappingFormatterMapping } from '@/hooks';
import { toFileId } from '@/hooks/api';
import { MappingFormatterProvider, MappingItem, useMappingFormatterStore, useModelConfigStore } from '@/store';
import { generateId, TokenCredentials, toNumber, toString } from '@/utils';

import { ConnectWithURLDialog } from './ConnectWithURLDialog';
import { FormatterTest } from './FormatterTest';
import { ImportFromCSVDialog } from './ImportFromCSVDialog';

const ColumnHeader = ({ header }: { header: string }) => {
  return (
    <div className="px-4 py-1">
      <Text className="!text-xs font-medium text-gray-500">{header}</Text>
    </div>
  );
};

const Tab = ({ children, ...props }: TabProps) => {
  return (
    <RACTab
      {...props}
      className="gap-sm flex grow cursor-pointer items-center justify-center rounded-md border p-1 outline-none not-data-[selected]:border-gray-100 data-[selected]:border-gray-200 data-[selected]:bg-white"
    >
      {children}
    </RACTab>
  );
};

type CellProps = {
  fieldId: string;
  formatterId: string;
  item: MappingItem;
  itemKey: keyof MappingItem;
  placeholder?: string;
};

const ICell = ({ fieldId, formatterId, item, itemKey, placeholder }: CellProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);
  const row = useMappingFormatterStore((state) => state.mapping.find((m) => m.id === item.id));
  const updateRow = useMappingFormatterStore((state) => state.updateRow);

  return (
    <TextField
      isDisabled={formatter?.config?.httpConfig != null}
      value={row?.[itemKey]}
      onChange={(value) => updateRow(item.id, { [itemKey]: value })}
    >
      <Input
        placeholder={placeholder}
        props={{
          container: {
            className: 'border-none shadow-none bg-transparent',
          },
        }}
      />
    </TextField>
  );
};

const Cell = memo(ICell) as typeof ICell;

type FormatterTableProps = {
  fieldId: string;
  formatterId: string;
  onSave: (mapping: MappingItem[]) => void;
};

const IFormatterTable = ({ fieldId, formatterId, onSave }: FormatterTableProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);
  const hasHttpConfig = formatter?.config?.httpConfig != null;
  const mapping = useMappingFormatterStore((state) => state.mapping);
  const addRow = useMappingFormatterStore((state) => state.addRow);
  const deleteRow = useMappingFormatterStore((state) => state.deleteRow);
  const moveRow = useMappingFormatterStore((state) => state.moveRow);
  const hasChanges = useMappingFormatterStore((state) => state.hasChanges);

  const columns: ColumnDef<MappingItem>[] = useMemo(() => {
    return [
      {
        header: () => <ColumnHeader header="From" />,
        accessorKey: 'from',
        cell: ({ row }) => (
          <Cell
            fieldId={fieldId}
            formatterId={formatterId}
            item={row.original}
            itemKey="from"
            placeholder="e.g. coca cola"
          />
        ),
      },
      {
        header: () => <ColumnHeader header="To" />,
        accessorKey: 'to',
        cell: ({ row }) => (
          <Cell
            fieldId={fieldId}
            formatterId={formatterId}
            item={row.original}
            itemKey="to"
            placeholder="e.g. The Coca-Cola Company"
          />
        ),
      },
    ];
  }, [fieldId, formatterId]);

  const onAddRow = useCallback(() => addRow({ id: generateId() }), [addRow]);

  return (
    <>
      <div className="rounded-lg border border-gray-200 shadow-xs">
        <Table
          className="w-full"
          props={{
            row: { className: 'group/row border-b border-gray-200 hover:bg-gray-25' },
            header: { className: 'rounded-t-lg bg-gray-50 border-b border-gray-200' },
          }}
          columns={columns}
          data={mapping}
          deleteRowAction={(row) => deleteRow(row.original.id)}
          hideDragHandle={hasHttpConfig}
          hideRowOptions={hasHttpConfig}
          onRowIndexChange={(oldIndex, newIndex) => moveRow(oldIndex, newIndex)}
        />
      </div>

      <div className="gap-sm flex items-center">
        <Button isDisabled={hasHttpConfig} variant="secondary" onPress={onAddRow}>
          <AddIcon />
          <Text>New row</Text>
        </Button>

        <div className="grow" />

        <Button isDisabled={!hasChanges} variant="primary" onPress={() => onSave(mapping)}>
          <Text>Save table changes</Text>
        </Button>
      </div>
    </>
  );
};

const FormatterTable = memo(IFormatterTable) as typeof IFormatterTable;

export type MappingFormatterProps = {
  fieldId: string;
  formatterId: string;
};

const IMappingFormatter = ({ fieldId, formatterId }: MappingFormatterProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const formatter = field?.formatters?.find((f) => f.id === formatterId);
  const hasHttpConfig = formatter?.config?.httpConfig != null;
  const { mapping, setMapping, isPending } = useMappingFormatterMapping({ fieldId, parentId: field?.parentId, formatterId });

  const mutate = useFormatterMutation({ fieldId, formatterId, parentId: field?.parentId });

  const onChangeLevenshteinThreshold = useCallback(
    (threshold: string) => {
      mutate((f) => (f.config.levenshteinThreshold = toNumber(threshold)));
    },
    [mutate]
  );

  const onChangeIgnoreCaseAndLocale = useCallback(
    (ignore: boolean) => {
      mutate((f) => (f.config.ignoreCaseAndLocale = ignore));
    },
    [mutate]
  );

  const [fuzzyMatchingEnabled, setFuzzyMatchingEnabled] = useState<Key>(
    toNumber(formatter?.config.levenshteinThreshold, 0) > 0 ? 'enabled' : 'disabled'
  );

  const onChangeEnableFuzzyMatching = useCallback(
    (key: Key) => {
      mutate((f) => {
        const enabled = key.toString() === 'enabled';
        if (enabled) {
          f.config.levenshteinThreshold = 1;
          f.config.ignoreCaseAndLocale = true;
        } else {
          f.config.levenshteinThreshold = 0;
          f.config.ignoreCaseAndLocale = false;
        }
        setFuzzyMatchingEnabled(key);
      });
    },
    [mutate]
  );

  const { client } = useClient();
  const queryClient = useQueryClient();

  const refreshMapping = useCallback(() => {
    const mappingFileUrl = formatter?.config?.mappingFileUrl as string | undefined;
    if (!mappingFileUrl) return;

    queryClient
      .invalidateQueries({
        queryKey: [(client.credentials as TokenCredentials).organizationId, 'files'],
        exact: true,
      })
      .catch(console.error);
    queryClient
      .invalidateQueries({
        queryKey: [(client.credentials as TokenCredentials).organizationId, 'files', toFileId(mappingFileUrl)],
        exact: true,
      })
      .catch(console.error);
  }, [client.credentials, formatter?.config, queryClient]);

  const [importFromCSVOpen, setImportFromCSVOpen] = useState(false);
  const [connectWithURLOpen, setConnectWithURLOpen] = useState(false);

  const onSetMapping = useCallback(
    (mapping: MappingItem[]) => {
      setMapping(mapping)
        .then(() => {
          setImportFromCSVOpen(false);
        })
        .catch(console.error);
    },
    [setMapping]
  );

  const onSetHttpConfig = useCallback(
    (httpConfig: JSONObject) => {
      mutate((f) => {
        f.config.httpConfig = httpConfig;
        setConnectWithURLOpen(false);
      });
    },
    [mutate]
  );

  const [formatterIdVersion, setFormatterIdVersion] = useState(`${fieldId}/${formatterId}/0`);
  const idempotencyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!mapping) return;

    const idempotency = `${fieldId}-${formatterId}-${JSON.stringify(mapping)}`;
    if (idempotency === idempotencyRef.current) return;

    idempotencyRef.current = idempotency;
    setFormatterIdVersion(`${fieldId}/${formatterId}/${Date.now()}`);
  }, [fieldId, formatterId, mapping]);

  const onRemoveHttpConfig = useCallback(() => {
    mutate((f) => (f.config.httpConfig = undefined));
  }, [mutate]);

  return (
    <>
      <ImportFromCSVDialog
        isOpen={importFromCSVOpen}
        onClose={() => setImportFromCSVOpen(false)}
        onImport={onSetMapping}
      />
      <ConnectWithURLDialog
        fieldId={fieldId}
        formatterId={formatterId}
        isOpen={connectWithURLOpen}
        onClose={() => setConnectWithURLOpen(false)}
        onConnect={onSetHttpConfig}
      />
      <div className="flex h-full flex-col gap-4 p-4">
        <FormatterValidatorHeader formatter={formatter} />

        <Separator orientation="horizontal" />

        <Tabs>
          <TabList className="p-xs gap-xs flex items-center justify-evenly rounded-lg bg-gray-100">
            <Tab id="table">
              <TableIcon />
              <Text size="xs" className="font-semibold">
                Table
              </Text>
            </Tab>

            <Tab id="settings">
              <SettingsIcon />
              <Text size="xs" className="font-semibold">
                Settings
              </Text>
            </Tab>
          </TabList>

          <TabPanel id="table">
            <div className="flex grow flex-col gap-4 px-2 pt-4">
              <div className="flex items-center">
                <Label>Mapping table</Label>

                <div className="grow" />

                {hasHttpConfig ? (
                  <TooltipTrigger>
                    <Focusable>
                      <Badge variant="success">
                        <div className="bg-success-500 size-2 rounded-full" />
                        <Text>Connected</Text>
                      </Badge>
                    </Focusable>

                    <Tooltip variant="primary">
                      <Text>
                        This table is connected to a remote URL and cannot be edited. Disconnect from URL to create a
                        custom table or import from CSV
                      </Text>
                    </Tooltip>
                  </TooltipTrigger>
                ) : null}

                {hasHttpConfig ? (
                  <TooltipTrigger>
                    <Button onPress={refreshMapping} variant="plain">
                      <RotateIcon rotation="cw" className="!size-3" />
                    </Button>

                    <Tooltip variant="primary">
                      <Text>Refresh table</Text>
                    </Tooltip>
                  </TooltipTrigger>
                ) : null}

                <MenuTrigger>
                  <Button variant="plain">
                    <OverflowIcon />
                  </Button>

                  <Popover>
                    <Menu>
                      {hasHttpConfig ? (
                        <>
                          <MenuItem onPress={() => setConnectWithURLOpen(true)}>
                            <LinkIcon />
                            <Text>Edit URL connection</Text>
                          </MenuItem>
                          <MenuItem variant="danger" onPress={onRemoveHttpConfig}>
                            <UnlinkIcon />
                            <Text>Remove URL connection</Text>
                          </MenuItem>
                        </>
                      ) : (
                        <>
                          <MenuItem onPress={() => setImportFromCSVOpen(true)}>
                            <CSVIcon />
                            <Text>Import from CSV</Text>
                          </MenuItem>
                          <MenuItem onPress={() => setConnectWithURLOpen(true)}>
                            <LinkIcon />
                            <Text>Connect to URL</Text>
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </Popover>
                </MenuTrigger>
              </div>

              {mapping ? (
                <MappingFormatterProvider formatterId={formatterIdVersion} mapping={mapping}>
                  <FormatterTable fieldId={fieldId} formatterId={formatterId} onSave={setMapping} />
                </MappingFormatterProvider>
              ) : !isPending ? (
                <>
                  <div className="gap-sm grid grid-cols-2">
                    <Button
                      variant="plain"
                      className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 py-4"
                      onPress={() => setImportFromCSVOpen(true)}
                    >
                      <CSVIconLarge className="size-12" />
                      <Text size="md">Upload CSV</Text>
                    </Button>
                    <Button
                      variant="plain"
                      className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 py-4"
                      onPress={() => setConnectWithURLOpen(true)}
                    >
                      <ConnectUrlIconLarge className="size-12" />
                      <Text size="md">Connect to URL</Text>
                    </Button>
                  </div>
                  <div className="gap-sm flex items-center">
                    <hr className="grow text-gray-200" />
                    <Text>Or create manually</Text>
                    <hr className="grow text-gray-200" />
                  </div>
                  <Button variant="secondary" onPress={() => setMapping([{ id: generateId() }])}>
                    <AddIcon />
                    <Text>Create table</Text>
                  </Button>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <Save message="Loading..." />
                </div>
              )}
            </div>
          </TabPanel>

          <TabPanel id="settings">
            <div className="flex grow flex-col gap-4 px-2 pt-4">
              <RadioGroup onChange={onChangeEnableFuzzyMatching} value={fuzzyMatchingEnabled.toString()}>
                <Radio value="disabled">
                  <div className="flex flex-col">
                    <Text slot="description">Exact match</Text>
                    <Text size="xs" className="!text-gray-500" slot="description">
                      Only returns results that match the text exactly.
                    </Text>
                  </div>
                </Radio>
                <Radio value="enabled">
                  <div className="flex flex-col">
                    <Text slot="description">Use fuzzy matching</Text>
                    <Text size="xs" className="!text-gray-500" slot="description">
                      Return results that are close to the text.
                    </Text>
                  </div>
                </Radio>
              </RadioGroup>

              {fuzzyMatchingEnabled.toString() === 'enabled' && (
                <>
                  <TextField
                    defaultValue={toString(formatter?.config.levenshteinThreshold, '0')}
                    onChange={onChangeLevenshteinThreshold}
                  >
                    <div className="gap-sm flex items-center">
                      <Label>Max levenshtein distance</Label>
                      <BasicTooltip>
                        <Text slot="description">
                          Control how strict or forgiving the matching should be. The number you set is the number of
                          typos or character changes you're willing to accept.
                        </Text>
                      </BasicTooltip>
                    </div>
                    <Input type="number" step={1} min={0} max={100} />
                  </TextField>

                  <div className="gap-sm flex">
                    <Checkbox
                      className="mt-1"
                      onChange={onChangeIgnoreCaseAndLocale}
                      isSelected={Boolean(formatter?.config.ignoreCaseAndLocale)}
                    />
                    <div className="flex flex-col">
                      <Text className="font-medium text-gray-700">Ignore case & locale</Text>
                      <Text className="text-gray-500">
                        Treats uppercase and lowercase letters as the same, using locale-aware rules for comparing text.
                      </Text>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabPanel>
        </Tabs>

        <div className="grow" />

        <FormatterTest fieldId={fieldId} parentId={field?.parentId} formatterId={formatterId} />
      </div>
    </>
  );
};

export const MappingFormatter = memo(IMappingFormatter) as typeof IMappingFormatter;
